import { HttpMethod } from '@libs/core';

import type { CartDiscountInterface, ShopCategoryForm, ShopCategoryResponse, ShopCriteriaItem } from '@/types';
import { formatShopCategoryForSubmit } from '@/utils/attributeCriteria';

export type ShopCategoryVerifyResponse = {
    activeCount: number;
    totalCount: number;
};

export type ProductTypeAttribute = {
    name: string;
    label: string;
};

export type FetchShopCategoryResult =
    | { success: true; data: ShopCategoryResponse; status: number }
    | { success: false; notFound: boolean; status: number };

export class ShopCategoryNotFoundError extends Error {
    readonly name = 'ShopCategoryNotFoundError';

    readonly status = 404;
}

export class ShopCategoryApiError extends Error {
    readonly name = 'ShopCategoryApiError';

    constructor(readonly status: number) {
        super(`Shop category API error (${status})`);
    }
}

export const fetchShopCategory = async (categoryKey: string): Promise<ShopCategoryResponse> => {
    let response: Response;

    try {
        response = await fetch(`/api/shopCategory?categoryKey=${encodeURIComponent(categoryKey)}`);
    } catch {
        throw new ShopCategoryApiError(500);
    }

    if (response.status === 404) {
        throw new ShopCategoryNotFoundError();
    }

    if (!response.ok) {
        throw new ShopCategoryApiError(response.status);
    }

    const body = await response.json();

    return body.data as ShopCategoryResponse;
};

export const submitShopCategory = async (values: ShopCategoryForm): Promise<ShopCategoryResponse> => {
    const payload = formatShopCategoryForSubmit(values);

    let response: Response;

    try {
        response = await fetch('/api/shopCategory', {
            method: HttpMethod.POST,
            body: JSON.stringify(payload),
        });
    } catch {
        throw new ShopCategoryApiError(500);
    }

    if (!response.ok) {
        throw new ShopCategoryApiError(response.status);
    }

    const { data } = await response.json();

    return data as ShopCategoryResponse;
};

export const fetchProductTypeAttributes = async (): Promise<ProductTypeAttribute[]> => {
    const response = await fetch('/api/productTypes/attributes');

    if (!response.ok) {
        throw new Error(`Failed to fetch product type attributes (${response.status})`);
    }

    const body = await response.json();
    return body.data ?? [];
};

export const verifyShopCriteriaGroup = async (
    group: ShopCriteriaItem[],
    market: 'US' | 'CA'
): Promise<ShopCategoryVerifyResponse> => {
    let response: Response;

    try {
        response = await fetch('/api/shopCategory/verify', {
            method: HttpMethod.POST,
            body: JSON.stringify({ group, market }),
        });
    } catch {
        throw new ShopCategoryApiError(500);
    }

    if (!response.ok) {
        throw new ShopCategoryApiError(response.status);
    }

    const { data } = await response.json();

    return data;
};

export const fetchAllCartDiscounts = async (country: 'US' | 'CA'): Promise<CartDiscountInterface[]> => {
    let response: Response;

    try {
        response = await fetch(`/api/shopCategory/cartDiscounts?country=${encodeURIComponent(country)}`);
    } catch {
        throw new ShopCategoryApiError(500);
    }

    if (!response.ok) {
        throw new ShopCategoryApiError(response.status);
    }

    const body = await response.json();
    return body.data ?? [];
};
